import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { peopleCoreAPI } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import type { Project, TimeAllocation } from '../types';
import { AxiosError } from 'axios';

// Extrai a mensagem de erro do backend
const getErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    // Tenta extrair a mensagem do backend
    const backendMessage = error.response?.data?.error;
    if (backendMessage && typeof backendMessage === 'string') {
      return backendMessage;
    }
  }

  // Fallback para mensagem genérica
  if (error instanceof Error) {
    return error.message;
  }

  return 'Erro desconhecido';
};

export function useProjects(showAll = false) {
  return useQuery<Project[]>({
    queryKey: ['people-core', 'projects', showAll],
    queryFn: async () => {
      const res = await peopleCoreAPI.listProjects(showAll ? { all: 'true' } : undefined);
      return res.data.data ?? [];
    },
  });
}

export function useProjectMutations() {
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['people-core', 'projects'] });

  const createProject = useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      peopleCoreAPI.createProject(data),
    onSuccess: () => {
      toast.success('Projeto criado!');
      invalidate();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const updateProject = useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; description?: string; isActive?: boolean }) =>
      peopleCoreAPI.updateProject(id, data),
    onSuccess: () => {
      toast.success('Projeto atualizado!');
      invalidate();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deleteProject = useMutation({
    mutationFn: (id: string) => peopleCoreAPI.deleteProject(id),
    onSuccess: () => {
      toast.success('Projeto excluído!');
      invalidate();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const createActivity = useMutation({
    mutationFn: ({ projectId, ...data }: { projectId: string; name: string; description?: string }) =>
      peopleCoreAPI.createActivity(projectId, data),
    onSuccess: () => {
      toast.success('Atividade criada!');
      invalidate();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const updateActivity = useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; description?: string; isActive?: boolean }) =>
      peopleCoreAPI.updateActivity(id, data),
    onSuccess: () => {
      toast.success('Atividade atualizada!');
      invalidate();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  return { createProject, updateProject, deleteProject, createActivity, updateActivity };
}

export function useTimeAllocations(
  params?: {
    startDate?: string;
    endDate?: string;
    projectId?: string;
    employeeId?: string;
  },
  options?: Omit<UseQueryOptions<TimeAllocation[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery<TimeAllocation[]>({
    queryKey: ['people-core', 'time-allocations', params],
    queryFn: async () => {
      const res = await peopleCoreAPI.listAllocations(params);
      return res.data.data ?? [];
    },
    ...options,
  });
}

export function useEmployeeAllocationMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['people-core', 'projects'] });

  const allocate = useMutation({
    mutationFn: (data: { employeeId: string; projectId: string }) =>
      peopleCoreAPI.allocateEmployee(data),
    onSuccess: () => {
      toast.success('Colaborador alocado!');
      invalidate();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deallocate = useMutation({
    mutationFn: ({ projectId, employeeId }: { projectId: string; employeeId: string }) =>
      peopleCoreAPI.deallocateEmployee(projectId, employeeId),
    onSuccess: () => {
      toast.success('Alocação removida!');
      invalidate();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  return { allocate, deallocate };
}

export function useTimeAllocationMutations() {
  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['people-core', 'time-allocations'] });

  const create = useMutation({
    mutationFn: (data: { activityId: string; date: string; hours: number; description?: string; employeeId?: string }) =>
      peopleCoreAPI.createAllocation(data),
    onSuccess: () => {
      toast.success('Lançamento registrado!');
      invalidate();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const update = useMutation({
    mutationFn: ({ id, ...data }: { id: string; hours?: number; description?: string }) =>
      peopleCoreAPI.updateAllocation(id, data),
    onSuccess: () => {
      toast.success('Lançamento atualizado!');
      invalidate();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => peopleCoreAPI.deleteAllocation(id),
    onSuccess: () => {
      toast.success('Lançamento removido!');
      invalidate();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  return { create, update, remove };
}
